import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { FaChevronLeft, FaChevronRight, FaPlay, FaPause, FaInstagram } from "react-icons/fa6";
import { useSelector, useDispatch } from "react-redux";
import { getVideos } from "../../slice/videoSlice";
import { getMediaUrl } from "../../common/mediaUrl";

const InstaMoments = () => {
  const { t, i18n } = useTranslation();
  const scrollRef = useRef(null);
  const dispatch = useDispatch();
  const { data: videos, loading } = useSelector((state) => state.video);
  const [playingId, setPlayingId] = useState(null);
  const videoRefs = useRef({});

  useEffect(() => {
    dispatch(getVideos());
  }, [dispatch, i18n.language]);

  const scroll = (direction) => {
    const { current } = scrollRef;
    if (current) {
      const scrollAmount = direction === "left" ? -400 : 400;
      current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  const handleVideoClick = (id) => {
    const video = videoRefs.current[id];
    if (playingId === id) {
      video.pause();
      video.muted = true;
      setPlayingId(null);
    } else {
      // Pause other videos if any
      if (playingId && videoRefs.current[playingId]) {
        videoRefs.current[playingId].pause();
        videoRefs.current[playingId].muted = true;
      }
      video.muted = false;
      video.play();
      setPlayingId(id);
    }
  };

  return (
    <section className="bg-white py-12 lg:py-20 overflow-hidden border-t border-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row gap-10 items-start">
          
          {/* LEFT SIDE: TEXT & CONTROLS (Top Aligned) */}
          <div className="lg:w-[30%] pt-6">
            <motion.a 
              href="https://www.instagram.com/routeskhajuraho/" 
              target="_blank"
              whileHover={{ scale: 1.05 }}
              className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full border border-gray-100 mb-6 hover:bg-orange-main hover:text-white hover:border-orange-main transition-all group shadow-sm bg-white"
            >
              <div className="w-7 h-7 rounded-full bg-gradient-to-tr from-yellow-400 via-red-500 to-purple-600 flex items-center justify-center text-white text-xs">
                <FaInstagram />
              </div>
              <span className="text-[9px] font-black uppercase tracking-widest text-gray-400 group-hover:text-orange-main">{t('insta_follow')}</span>
            </motion.a>

            <h2 className="text-3xl lg:text-4xl font-black text-orange-main leading-tight mb-8 tracking-tighter">
              {t('insta_label')} <br />
              <span className="text-gray-dark">{t('insta_heading')}</span>
            </h2>

            <div className="flex items-center gap-3">
              <button 
                onClick={() => scroll("left")}
                className="w-11 h-11 rounded-full border border-gray-100 flex items-center justify-center text-gray-400 hover:bg-orange-main hover:text-white transition-all shadow-sm"
              >
                <FaChevronLeft />
              </button>
              <button 
                onClick={() => scroll("right")}
                className="w-11 h-11 rounded-full border border-gray-100 flex items-center justify-center text-gray-400 hover:bg-orange-main hover:text-white transition-all shadow-sm"
              >
                <FaChevronRight />
              </button>
            </div>
          </div>

          {/* RIGHT SIDE: SLIDER */}
          <div className="lg:w-[70%]">
            <div 
              ref={scrollRef}
              className="flex gap-5 overflow-x-auto no-scrollbar pb-8 snap-x snap-mandatory"
            >
              {videos && videos.length > 0 ? videos.map((item) => (
                <motion.div
                  key={item.id}
                  className="min-w-[260px] sm:min-w-[300px] lg:min-w-[calc((100%-40px)/3)] aspect-[9/16] rounded-[2rem] overflow-hidden relative shadow-xl snap-start cursor-pointer group bg-gray-100"
                  onClick={() => handleVideoClick(item.id)}
                >
                  <video 
                    ref={(el) => (videoRefs.current[item.id] = el)}
                    src={getMediaUrl(item.videoPath)} 
                    className="w-full h-full object-cover" 
                    loop 
                    muted 
                    playsInline
                  />
                  
                  {/* Play/Pause Hover State */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 bg-black/20">
                    <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-xl border border-white/30 flex items-center justify-center text-white shadow-2xl scale-90 group-hover:scale-100 transition-transform duration-500">
                      {playingId === item.id ? (
                        <FaPause className="text-xl" />
                      ) : (
                        <FaPlay className="ml-1 text-xl" />
                      )}
                    </div>
                  </div>
                  
                  {/* Playing Indicator */}
                  {playingId === item.id && (
                    <div className="absolute top-6 right-6 flex gap-1">
                      <motion.div 
                        animate={{ height: [8, 16, 8] }} 
                        transition={{ repeat: Infinity, duration: 0.5 }}
                        className="w-1 bg-white rounded-full" 
                      />
                      <motion.div 
                        animate={{ height: [16, 8, 16] }} 
                        transition={{ repeat: Infinity, duration: 0.5, delay: 0.1 }}
                        className="w-1 bg-white rounded-full" 
                      />
                      <motion.div 
                        animate={{ height: [10, 18, 10] }} 
                        transition={{ repeat: Infinity, duration: 0.5, delay: 0.2 }}
                        className="w-1 bg-white rounded-full" 
                      />
                    </div>
                  )}
                </motion.div>
              )) : !loading && <div className="text-center w-full py-10 text-gray-400">{t('insta_empty')}</div>}
            </div>
          </div>

        </div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}} />
    </section>
  );
};

export default InstaMoments;
